<template>
  <v-row class="mx-5 my-0 q-marketing-resources" cols="12" width="100%">
    <div class="q-marketing-resources__controls py-4">
      <div class="q-marketing-resources__search mb-4">
        <v-text-field
          v-model="filters.fileName"
          prepend-icon="fas fa-search"
          hide-details
          class="q-marketing-resources__search-input"
          placeholder="Search by the file name, description or key word"
        />
      </div>

      <v-row class="q-marketing-resources__filters px-4">
        <v-col cols="12" md="4">
          <v-select label="File Type" v-model="filters.type" :items="filtersVariants.types" clearable />
        </v-col>
        <v-col cols="12" md="4">
          <v-select label="Subject" v-model="filters.subject" :items="filtersVariants.subjects" clearable />
        </v-col>
        <v-col cols="12" md="4">
          <div class="d-flex w-full justify-center">
            <v-btn-toggle class="q-marketing-resources__audience-switch">
              <v-btn color="#0083EB" @click="toggleAudience('Agent')">
                Agent
              </v-btn>
              <v-btn color="#46C3B2" dark @click="toggleAudience('Customer')">
                Customer
              </v-btn>
            </v-btn-toggle>
          </div>
        </v-col>
      </v-row>

      <v-divider />
    </div>

    <template v-if="isLoading">
      <VProgressLinear indeterminate color="primary" />
    </template>

    <template v-else>
      <div class="my-4">
        <v-btn depressed color="primary" :disabled="!selectedItemsIDs.length" @click="downloadBulkSelected">Download Selected</v-btn>
      </div>

      <template v-for="(items, group) in groupedItems">
        <div
          class="q-marketing-resources__group"
          v-if="items.length > 0"
          :key="group"
        >
          <h4 class="q-title px-4" style="font-size: 18px; font-weight: 600 !important;">{{ group }}</h4>

          <MarketingResourceTable
            :items="filterItems(items)"
            :selected="selected[group]"
            @update:selected="selected[group] = $event"
          />
        </div>
      </template>
    </template>
  </v-row>
</template>

<script>
import QuilityAPI from "../../../store/API/QuilityAPI.js";
import MarketingResourceTable from "../../../components/document_library/marketing_resource/Table.vue";
import { namedExtensions } from "../../../components/document_library/fileTypes.ts";

const filterTypes = namedExtensions

export default {
  name: "MarketingResource",

  components: { MarketingResourceTable },

  props: {
    contentData: { type: Object, required: true },
  },

  data() {
    return {
      isLoading: false,
      groupedItems: {},
      filters: {
        subject: '',
        type: '',
        fileName: '',
        audience: ''
      },
      // Groups and tags can be removed, but it will be stored in CMS and will be filtered out
      filteredContentData: {
        groups: [],
        tags: [],
      },
      selected: {},
    };
  },

  async mounted() {
    this.isLoading = true;
    Promise.all([ await QuilityAPI.getMarketingResourcesGroupsList(), await QuilityAPI.getMarketingResourcesTagsList()])
      .then(([groups, tags]) => {
        this.filteredContentData.groups = this.contentData.groups.filter((group) => groups.includes(group));
        this.filteredContentData.tags = this.contentData.tags.filter((tag) => tags.includes(tag));
      })
    this.isLoading = false;
  },

  computed: {
    tags() {
      return this.filteredContentData.tags
    },
    groups() {
      return this.filteredContentData.groups
    },
    filtersVariants() {
      const allItems = Object.values(this.groupedItems).flat()
      const filters = {
        subjects: [],
        types: [],
      }

      allItems.forEach((item) => {
        if (!filters.subjects.includes(item.DocumentTags)) {
          filters.subjects.push(...(item.DocumentTags || []))
        }

        Object.entries(filterTypes).forEach(([type, extensions]) => {
          if (extensions.includes(item.DocType)) {
            if (!filters.types.includes(type)) {
              filters.types.push(type)
            }
          }
        })
      })

      return filters
    },
    selectedItemsIDs () {
      return Object.values(this.selected).flat().map((item) => item.ID)
    },
  },

  methods: {
    filterItems(items) {
      return items
        .filter((item) => {
          return item.DocTitle.toLowerCase().includes(this.filters.fileName.toLowerCase()) || item.DocDesc?.toLowerCase().includes(this.filters.fileName.toLowerCase())
        })
        .filter((item) => {
          return this.filters.subject ? item.DocumentTags.includes(this.filters.subject) : true;
        })
        .filter((item) => {
          return this.filters.type ? filterTypes[this.filters.type].includes(item.DocType) : true;
        })
        .filter((item) => {
          return this.filters.audience ? item.Audience === this.filters.audience : true;
        })
    },
    toggleAudience(to) {
      if (this.filters.audience === to) {
        this.filters.audience = ''
        return
      }

      this.filters.audience = to
    },
    downloadBulkSelected() {
      if (!this.selectedItemsIDs.length) { return }
      const endpoint = '/api/private/marketing_resource_library/bulk-download'
      const params = {
          'itemsPerPage': 'zip',
          'DocumentIds': this.selectedItemsIDs,
      }
      const job = {
          'method': 'GET',
          'path': endpoint,
          'params': params,
          'JobName': 'MRL Export',
      }
      this.dispatchJob(job)
    },
  },

  watch: {
    filteredContentData: {
      handler: async function (newContentData, oldContentData) {
        this.isLoading = true;
        newContentData.groups.forEach(async (group) => {
          this.$set(this.groupedItems, group, await QuilityAPI.getMarketingResources(group, newContentData.tags))
          this.$set(this.selected, group, [])
        })
        this.isLoading = false;
      },
      immediate: true,
      deep: true,
    },
  }
};
</script>

<style lang="scss">
  .q-marketing-resources {
    border: 1px solid #ccc;
    padding: 1rem;

    &__controls {
      width: 100%;
    }

    &__filters {
      width: 100%;
      display: flex;
      justify-content: space-evenly;
      align-items: center;
    }

    &__audience-switch {
      .v-btn__content {
        color: white !important;
      }

      &:has(.v-btn--active) {
        .v-btn:not(.v-btn--active) {
          opacity: 0.5 !important;
        }
      }

      .v-btn--active {
        font-weight: 600 !important;
      }
    }

    &__search {
      max-width: 600px;
      margin: auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    &__search-input {
      width: 100%;
    }

    &__group {
      margin-top: 1rem;
      width: 100%;
    }

    .v-input {
      input {
        &::-webkit-input-placeholder, &:-moz-placeholder, &:-ms-input-placeholder, &::-ms-input-placeholder, &:placeholder-shown, &::placeholder { /* Safari, Chrome and Opera */
          opacity: 0.5;
        }
      }
    }
  }
</style>